import { Game } from '@Game/Game';
import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETurboModeLevels } from '@GFTypes/GameStoreTypes';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EWinType } from '@Managers/Redux/HttpState';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { getCurrentTurboLevel } from '@Managers/TurboManager/TurboManager';
import { SoundManager } from './SoundManager';

export default class WinSoundsManager {
    public static readonly WIN_SOUND: string = 'win_';
    private readonly game: Game;
    private readonly manager: SoundManager;
    private currentWinSound: string;

    constructor(game: Game, manager: SoundManager) {
        Logger.log(LoggerLevel.Constructor, {
            class: 'WinSoundsManager',
            message: 'Win Sounds Manager constructor',
            source: LoggerSource.Game
        });
        this.game = game;
        this.manager = manager;
    }

    public playWinSound(): void {
        const finalWinLevelName = this.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState)?.finalWinLevelName;
        const winLevelIndex = this.game.gameStore.get(EGameStoreKeys.BaseWinPresentationState).finalWinLevelIndex + 1;
        const isBigWin = finalWinLevelName !== EWinType.NORMAL_WIN;
        this.currentWinSound = isBigWin ? finalWinLevelName?.toLowerCase() : `${WinSoundsManager.WIN_SOUND}${String(winLevelIndex).padStart(2, '0')}`;
        const { active } = this.game.gameStore.get(EGameStoreKeys.TurboModeState);
        const turboLevel = getCurrentTurboLevel(this.game.gameStore);
        const isTurbo = active && turboLevel === ETurboModeLevels.TURBO;
        const sounds = this.game.gameStore.get(EGameStoreKeys.SoundsConfiguration).sounds;
        const { turboRate } = sounds[this.currentWinSound];
        this.manager.playSound(this.currentWinSound, { rate: isTurbo && turboRate ? turboRate : 1 });
    }

    public stopCurrentWinSound(): void {
        if (this.currentWinSound) this.manager.stopSound(this.currentWinSound);
    }

    public pauseCurrentWinSound(): void {
        if (this.currentWinSound) this.manager.pauseSound(this.currentWinSound);
    }

    public resumeCurrentWinSound(): void {
        if (this.currentWinSound) this.manager.resumeSound(this.currentWinSound);
    }

    public onWinPresentationHidden(): void {
        this.stopCurrentWinSound();

        if (gameInstance.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.POST_WIN_STATE) {
            this.manager.playSound('gamble_anticipation', { loop: true });
        }
    }

    public getWinLevelSoundName(winLevelId: number): string {
        const levelConfig = this.game.gameStore.get(EGameStoreKeys.WinLevelsConfiguration)[winLevelId];
        const name = levelConfig?.name ?? EWinType.NORMAL_WIN;

        if (name === EWinType.NORMAL_WIN) {
            return `win_${(winLevelId + 1).toString().padStart(2, '0')}`;
        }

        return name.toLowerCase();
    }

    public getWinLevelSoundDuration(winLevelId: number): number {
        return (SoundManager.getSoundDuration(this.getWinLevelSoundName(winLevelId)) ?? 0) * 1000;
    }
}
